/**
 * Gallery Lightbox Component
 * Lightbox con zoom y navegación para product-gallery.liquid
 */

if (typeof GalleryLightbox !== 'function') {

  class GalleryLightbox {
    constructor() {
      this.images = [];
      this.currentIndex = 0;
      this.isOpen = false;
      this.scale = 1;
      this.minScale = 1;
      this.maxScale = 3;
      this.translateX = 0;
      this.translateY = 0;
      this.lastTouchDistance = 0;
      this.isDragging = false;
      this.startX = 0;
      this.startY = 0;
      this.lastTranslateX = 0;
      this.lastTranslateY = 0;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.swipeThreshold = 50;
      this.lastTapTime = 0;
      this.doubleTapDelay = 300;

      this.createLightbox();
      this.bindEvents();
    }

    createLightbox() {
      // Crear estructura del lightbox
      this.lightbox = document.createElement('div');
      this.lightbox.id = 'product-lightbox';
      this.lightbox.className = 'product-lightbox';
      this.lightbox.innerHTML = `
        <div class="product-lightbox__overlay"></div>
        <button class="product-lightbox__close" aria-label="Cerrar">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        <button class="product-lightbox__nav product-lightbox__nav--prev" aria-label="Anterior">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button class="product-lightbox__nav product-lightbox__nav--next" aria-label="Siguiente">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
        <div class="product-lightbox__image-wrapper">
          <img class="product-lightbox__image" src="" alt="" />
        </div>
        <div class="product-lightbox__counter"></div>
        <div class="product-lightbox__loader">
          <svg width="40" height="40" viewBox="0 0 40 40">
            <circle cx="20" cy="20" r="18" fill="none" stroke="currentColor" stroke-width="2" stroke-dasharray="90 30" stroke-linecap="round">
              <animateTransform attributeName="transform" type="rotate" from="0 20 20" to="360 20 20" dur="1s" repeatCount="indefinite"/>
            </circle>
          </svg>
        </div>
      `;

      document.body.appendChild(this.lightbox);

      // Referencias a elementos
      this.overlay = this.lightbox.querySelector('.product-lightbox__overlay');
      this.closeBtn = this.lightbox.querySelector('.product-lightbox__close');
      this.prevBtn = this.lightbox.querySelector('.product-lightbox__nav--prev');
      this.nextBtn = this.lightbox.querySelector('.product-lightbox__nav--next');
      this.imageWrapper = this.lightbox.querySelector('.product-lightbox__image-wrapper');
      this.image = this.lightbox.querySelector('.product-lightbox__image');
      this.counter = this.lightbox.querySelector('.product-lightbox__counter');
      this.loader = this.lightbox.querySelector('.product-lightbox__loader');
    }

    bindEvents() {
      // Cerrar lightbox
      this.closeBtn.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());

      // Navegación
      this.prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.prev();
      });
      this.nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.next();
      });

      // Teclado
      document.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'ArrowLeft') this.prev();
        if (e.key === 'ArrowRight') this.next();
      });

      // Zoom con scroll del mouse (desktop)
      this.imageWrapper.addEventListener('wheel', (e) => {
        if (!this.isOpen) return;
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.2 : 0.2;
        this.zoom(delta, e.clientX, e.clientY);
      }, { passive: false });

      // Doble click para zoom (desktop)
      this.image.addEventListener('dblclick', (e) => {
        e.preventDefault();
        if (this.scale > 1) {
          this.resetZoom();
        } else {
          this.zoom(1, e.clientX, e.clientY);
        }
      });

      // Touch events para móvil
      this.imageWrapper.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: false });
      this.imageWrapper.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
      this.imageWrapper.addEventListener('touchend', (e) => this.handleTouchEnd(e));

      // Mouse drag para pan (desktop)
      this.imageWrapper.addEventListener('mousedown', (e) => this.handleMouseDown(e));
      document.addEventListener('mousemove', (e) => this.handleMouseMove(e));
      document.addEventListener('mouseup', () => this.handleMouseUp());

      // Carga de imagen
      this.image.addEventListener('load', () => {
        this.loader.classList.remove('product-lightbox__loader--visible');
        this.image.classList.add('product-lightbox__image--loaded');
      });
    }

    /**
     * Configura las imágenes directamente (método preferido)
     * @param {Array} images - Array de objetos {src, alt}
     */
    setImages(images) {
      this.images = images.map(img => ({
        src: this.getHighResSrc(img.src),
        alt: img.alt || 'Imagen del producto'
      }));
    }

    /**
     * Obtiene la URL de alta resolución de una imagen de Shopify
     */
    getHighResSrc(src) {
      if (!src) return '';
      // Remover parámetros de tamaño de Shopify
      let highRes = src.replace(/_\d+x\d*(\.[a-z]+)/i, '_2000x$1');
      // Si no tenía parámetro de tamaño, agregarlo antes de la extensión
      if (highRes === src && /\.(jpg|jpeg|png|webp|gif)/i.test(src)) {
        highRes = src.replace(/(\.[a-z]+)(\?.*)?$/i, '_2000x$1$2');
      }
      return highRes;
    }

    collectImages(container) {
      this.images = [];

      // Buscar imágenes en el contenedor de galería
      const desktopImages = container.querySelectorAll('.prod-grid-hero__image-item img, .prod-grid-hero__images img');
      const mobileSlides = container.querySelectorAll('.prod-grid-hero__carousel-slide');

      // Usar las imágenes visibles según el viewport
      const isMobile = window.innerWidth < 768;

      if (isMobile && mobileSlides.length > 0) {
        // En móvil, los slides son directamente elementos img
        mobileSlides.forEach(slide => {
          const src = slide.src || slide.dataset.src;
          if (src) {
            this.images.push({
              src: this.getHighResSrc(src),
              alt: slide.alt || 'Imagen del producto'
            });
          }
        });
      } else if (desktopImages.length > 0) {
        desktopImages.forEach(img => {
          const src = img.src || img.dataset.src;
          if (src) {
            this.images.push({
              src: this.getHighResSrc(src),
              alt: img.alt || 'Imagen del producto'
            });
          }
        });
      }

      return this.images.length;
    }

    /**
     * Abre el lightbox
     * @param {number} index - Índice de la imagen a mostrar
     * @param {HTMLElement|Array} containerOrImages - Contenedor DOM o array de imágenes
     */
    open(index = 0, containerOrImages = document) {
      // Si se pasa un array de imágenes directamente
      if (Array.isArray(containerOrImages)) {
        this.setImages(containerOrImages);
      } else {
        // Buscar imágenes en el contenedor
        if (this.collectImages(containerOrImages) === 0) return;
      }

      if (this.images.length === 0) return;

      this.currentIndex = Math.min(index, this.images.length - 1);
      this.isOpen = true;
      this.lightbox.classList.add('product-lightbox--open');
      document.body.style.overflow = 'hidden';

      this.loadImage(this.currentIndex);
      this.updateCounter();
      this.updateNavButtons();
    }

    close() {
      this.isOpen = false;
      this.lightbox.classList.remove('product-lightbox--open');
      document.body.style.overflow = '';
      this.resetZoom();
      this.image.classList.remove('product-lightbox__image--loaded');
    }

    loadImage(index) {
      this.loader.classList.add('product-lightbox__loader--visible');
      this.image.classList.remove('product-lightbox__image--loaded');

      const imageData = this.images[index];
      this.image.src = imageData.src;
      this.image.alt = imageData.alt;

      this.resetZoom();
    }

    prev() {
      if (this.images.length <= 1) return;
      this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
      this.loadImage(this.currentIndex);
      this.updateCounter();
    }

    next() {
      if (this.images.length <= 1) return;
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
      this.loadImage(this.currentIndex);
      this.updateCounter();
    }

    updateCounter() {
      this.counter.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
    }

    updateNavButtons() {
      const showNav = this.images.length > 1;
      this.prevBtn.style.display = showNav ? '' : 'none';
      this.nextBtn.style.display = showNav ? '' : 'none';
    }

    zoom(delta, clientX, clientY) {
      const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale + delta));

      if (newScale !== this.scale) {
        // Calcular el punto de zoom relativo a la imagen
        const rect = this.imageWrapper.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const pointX = clientX - rect.left;
        const pointY = clientY - rect.top;

        // Ajustar la traslación para mantener el punto bajo el cursor
        if (newScale > this.scale) {
          const scaleDiff = newScale / this.scale;
          this.translateX = (this.translateX - pointX) * scaleDiff + pointX;
          this.translateY = (this.translateY - pointY) * scaleDiff + pointY;
        }

        this.scale = newScale;
        this.applyTransform();
      }
    }

    resetZoom() {
      this.scale = 1;
      this.translateX = 0;
      this.translateY = 0;
      this.applyTransform();
    }

    applyTransform() {
      // Limitar el pan para que la imagen no salga del viewport
      if (this.scale > 1) {
        const rect = this.imageWrapper.getBoundingClientRect();
        const maxTranslateX = (this.scale - 1) * rect.width / 2;
        const maxTranslateY = (this.scale - 1) * rect.height / 2;

        this.translateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, this.translateX));
        this.translateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, this.translateY));
      } else {
        this.translateX = 0;
        this.translateY = 0;
      }

      this.image.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
    }

    // Touch handlers para móvil
    handleTouchStart(e) {
      if (e.touches.length === 2) {
        // Pinch zoom inicio
        this.lastTouchDistance = this.getTouchDistance(e.touches);
        e.preventDefault();
      } else if (e.touches.length === 1) {
        this.touchStartX = e.touches[0].clientX;
        this.touchStartY = e.touches[0].clientY;
        this.lastTranslateX = this.translateX;
        this.lastTranslateY = this.translateY;

        // Detectar doble tap
        const currentTime = new Date().getTime();
        if (currentTime - this.lastTapTime < this.doubleTapDelay) {
          e.preventDefault();
          if (this.scale > 1) {
            this.resetZoom();
          } else {
            this.zoom(1, e.touches[0].clientX, e.touches[0].clientY);
          }
        }
        this.lastTapTime = currentTime;
      }
    }

    handleTouchMove(e) {
      if (e.touches.length === 2) {
        // Pinch zoom
        e.preventDefault();
        const distance = this.getTouchDistance(e.touches);
        const delta = (distance - this.lastTouchDistance) * 0.01;
        const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        this.zoom(delta, centerX, centerY);
        this.lastTouchDistance = distance;
      } else if (e.touches.length === 1) {
        const deltaX = e.touches[0].clientX - this.touchStartX;
        const deltaY = e.touches[0].clientY - this.touchStartY;

        if (this.scale > 1) {
          // Pan cuando está con zoom
          e.preventDefault();
          this.translateX = this.lastTranslateX + deltaX;
          this.translateY = this.lastTranslateY + deltaY;
          this.applyTransform();
        }
      }
    }

    handleTouchEnd(e) {
      if (e.touches.length === 0 && this.scale === 1) {
        // Detectar swipe para navegación
        const deltaX = e.changedTouches[0].clientX - this.touchStartX;
        const deltaY = e.changedTouches[0].clientY - this.touchStartY;

        // Solo swipe horizontal si el movimiento es más horizontal que vertical
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > this.swipeThreshold) {
          if (deltaX > 0) {
            this.prev();
          } else {
            this.next();
          }
        }
      }
    }

    getTouchDistance(touches) {
      return Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY
      );
    }

    // Mouse handlers para desktop
    handleMouseDown(e) {
      if (this.scale > 1) {
        this.isDragging = true;
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.lastTranslateX = this.translateX;
        this.lastTranslateY = this.translateY;
        this.imageWrapper.style.cursor = 'grabbing';
        e.preventDefault();
      }
    }

    handleMouseMove(e) {
      if (!this.isDragging) return;

      const deltaX = e.clientX - this.startX;
      const deltaY = e.clientY - this.startY;

      this.translateX = this.lastTranslateX + deltaX;
      this.translateY = this.lastTranslateY + deltaY;
      this.applyTransform();
    }

    handleMouseUp() {
      this.isDragging = false;
      this.imageWrapper.style.cursor = '';
    }
  }

  // Crear instancia singleton
  window.galleryLightbox = new GalleryLightbox();

  // Función helper para inicializar en galerías
  window.initGalleryLightbox = function(galleryContainer) {
    if (!galleryContainer) return;

    // Buscar todas las imágenes clickeables
    const clickableImages = galleryContainer.querySelectorAll('.prod-grid-hero__image, .prod-grid-hero__carousel-slide, [data-lightbox-trigger]');

    clickableImages.forEach((element, index) => {
      element.style.cursor = 'zoom-in';
      element.addEventListener('click', (e) => {
        e.preventDefault();
        window.galleryLightbox.open(index, galleryContainer);
      });
    });

    // También buscar botones de zoom si existen
    const zoomButtons = galleryContainer.querySelectorAll('.gallery-zoom-trigger');
    zoomButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const index = parseInt(btn.dataset.index) || 0;
        window.galleryLightbox.open(index, galleryContainer);
      });
    });
  };

}
